import React from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, PanelRightClose, PanelRightOpen, UserCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { ProfileAvatar } from "@/components/common/ProfileAvatar";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { useRefreshParticipantPicture } from "@/hooks/inbox/useRefreshParticipantPicture";
import { ChannelBadge } from "./ChannelBadge";
import { getChannel, getConversationTitle, getPrimaryParticipant } from "./utils";
import type { InboxConversation, InboxParticipant, MatchedCandidate } from "@/types/inbox";

interface ThreadHeaderProps {
	conversation: InboxConversation;
	participants: InboxParticipant[];
	matchedCandidate?: MatchedCandidate | null;
	onBack?: () => void;
	contextPanelOpen?: boolean;
	onToggleContextPanel?: () => void;
}

/** Sticky header above the thread: who, which channel, quick actions. */
export const ThreadHeader: React.FC<ThreadHeaderProps> = ({
	conversation,
	participants,
	matchedCandidate,
	onBack,
	contextPanelOpen,
	onToggleContextPanel,
}) => {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const channel = getChannel(conversation);
	const title = getConversationTitle(conversation, participants);
	const primary = getPrimaryParticipant(conversation, participants);
	const refreshPicture = useRefreshParticipantPicture();

	return (
		<header className="flex shrink-0 items-start gap-3 border-b border-border bg-background px-4 py-3">
			{onBack && (
				<Button
					variant="ghost"
					size="icon"
					className="mt-0.5 h-8 w-8 shrink-0 -ml-1"
					onClick={onBack}
					aria-label={t("inbox.back", "Back")}
				>
					<ArrowLeft className="h-4 w-4" />
				</Button>
			)}
			<ProfileAvatar
				src={primary?.picture_url}
				alt={title}
				className="h-9 w-9 shrink-0"
				participantId={primary?.id}
				conversationId={conversation.id}
				onImageError={refreshPicture}
			/>
			<div className="min-w-0 flex-1">
				{/* Name gets the full first line — badges and actions live below. */}
				<h2 className="truncate text-sm font-semibold text-foreground">{title}</h2>
				{channel === "email" && conversation.subject && (
					<p className="truncate text-xs text-muted-foreground">{conversation.subject}</p>
				)}
				<div className="mt-1.5 flex flex-wrap items-center gap-1.5">
					<ChannelBadge channel={channel} />
					{matchedCandidate && (
						<Tooltip>
							<TooltipTrigger asChild>
								<Button
									variant="outline"
									size="sm"
									className="h-7 gap-1.5 px-2 text-xs"
									onClick={() => navigate(`/candidates/${matchedCandidate.id}`)}
								>
									<UserCheck className="h-3.5 w-3.5" />
									{t("inbox.context.openCandidate", "Open candidate")}
								</Button>
							</TooltipTrigger>
							<TooltipContent side="bottom">
								{matchedCandidate.name || t("inbox.context.openCandidate", "Open candidate")}
							</TooltipContent>
						</Tooltip>
					)}
					{onToggleContextPanel && (
						<Tooltip>
							<TooltipTrigger asChild>
								<Button
									variant="ghost"
									size="icon"
									className="h-7 w-7 text-muted-foreground"
									onClick={onToggleContextPanel}
									aria-label={t("inbox.toggleContext", "Toggle details panel")}
								>
									{contextPanelOpen ? (
										<PanelRightClose className="h-4 w-4" />
									) : (
										<PanelRightOpen className="h-4 w-4" />
									)}
								</Button>
							</TooltipTrigger>
							<TooltipContent side="bottom">
								{t("inbox.toggleContext", "Toggle details panel")}
							</TooltipContent>
						</Tooltip>
					)}
				</div>
			</div>
		</header>
	);
};
